import { useState } from "react";

import dayjs from "dayjs";
import styled from "styled-components";

import { ChevronDown, ChevronRight, GoogleSearch } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { IGoogleAdsKeywordMutateResult } from "app/pages/digital-marketing/shared/google-ads/services/google-ads-proxy-client";

import type { GoogleAdsActionType } from "./google-ads-actions.types";

// SIM-115: a lightweight, SESSION-ONLY record of one confirmed Google Ads write action. Owned as
// in-memory state by MonitorKeywordsPage - nothing here is persisted, so a reload/navigation away
// clears the log. This is purely to make the write functionality visibly provable on camera, not
// an audit trail.
export interface IGoogleAdsWriteActionLogEntry {
    id: string;
    actionType: GoogleAdsActionType;
    timestamp: number;
    addedCount: number;
    duplicateCount: number;
    errorCount: number;
}

interface IGoogleAdsWriteActionLogProps {
    entries: IGoogleAdsWriteActionLogEntry[];
}

const ACTION_TYPE_LABEL: Record<GoogleAdsActionType, string> = {
    add: "Added to Google Ads",
    negative: "Added as negative keywords",
    remove: "Removed from Google Ads",
};

const ACTION_TYPE_DOT_COLOR: Record<GoogleAdsActionType, string> = {
    add: colorsPalettes.blue[400],
    negative: colorsPalettes.orange[400],
    remove: colorsPalettes.red[400],
};

/** Derives the per-status counts a log entry summarizes from a completed write mutation's raw
 * per-keyword results (SIM-114's `IGoogleAdsKeywordMutateResult[]`). Kept next to the entry type
 * it feeds so the counting logic and the shape it produces stay in one place. */
export const countGoogleAdsWriteResults = (
    results: IGoogleAdsKeywordMutateResult[],
): Pick<IGoogleAdsWriteActionLogEntry, "addedCount" | "duplicateCount" | "errorCount"> =>
    results.reduce(
        (counts, result) => {
            if (result.status === "success") {
                counts.addedCount += 1;
            } else if (result.status === "duplicate") {
                counts.duplicateCount += 1;
            } else {
                counts.errorCount += 1;
            }
            return counts;
        },
        { addedCount: 0, duplicateCount: 0, errorCount: 0 },
    );

const summarizeOutcome = (entry: IGoogleAdsWriteActionLogEntry): string => {
    // `addedCount` counts successful mutations regardless of direction, so a "remove" entry reads
    // "N removed" rather than "N added".
    const successVerb = entry.actionType === "remove" ? "removed" : "added";
    const parts: string[] = [];
    if (entry.addedCount > 0) {
        parts.push(`${entry.addedCount} ${successVerb}`);
    }
    if (entry.duplicateCount > 0) {
        parts.push(`${entry.duplicateCount} already existed`);
    }
    if (entry.errorCount > 0) {
        parts.push(`${entry.errorCount} failed`);
    }
    return parts.join(", ");
};

const LogWrapper = styled(Stack)`
    max-width: 420px;
    border: 1px solid ${colorsPalettes.carbon[50]};
    border-radius: 4px;
    background-color: ${colorsPalettes.carbon[0]};
    margin-bottom: 16px;
`;

const LogHeader = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 12px;
    margin: 0;
    border: none;
    background: none;
    font: inherit;
    text-align: left;
    cursor: pointer;
`;

const LogEntriesList = styled(Stack)`
    padding: 0 12px 10px;
`;

const ActionDot = styled.span<{ $color: string }>`
    width: 8px;
    height: 8px;
    margin-top: 4px;
    border-radius: 50%;
    flex-shrink: 0;
    background-color: ${({ $color }) => $color};
`;

/** Presentational, collapsible session log of confirmed Google Ads write actions, rendered next
 * to the SIM-108 "Google Ads imported" badge on the keyword list detail page. Renders nothing
 * when there are no entries yet, so the page stays uncluttered until a write action happens. */
export const GoogleAdsWriteActionLog = ({ entries }: IGoogleAdsWriteActionLogProps) => {
    const [isExpanded, setIsExpanded] = useState(true);

    if (entries.length === 0) {
        return null;
    }

    return (
        <LogWrapper
            gap="0"
            data-automation="google-ads-write-action-log"
        >
            <LogHeader
                type="button"
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded((expanded) => !expanded)}
                data-automation="google-ads-write-action-log-toggle"
            >
                <Flex
                    alignItems="center"
                    gap="6px"
                >
                    <GoogleSearch
                        size="xs"
                        color={colorsPalettes.carbon[300]}
                    />
                    <Text
                        fontVariant="subtitle2"
                        color={colorsPalettes.carbon[500]}
                    >
                        Google Ads activity ({entries.length})
                    </Text>
                </Flex>
                {isExpanded ? (
                    <ChevronDown
                        size="xs"
                        color={colorsPalettes.carbon[300]}
                    />
                ) : (
                    <ChevronRight
                        size="xs"
                        color={colorsPalettes.carbon[300]}
                    />
                )}
            </LogHeader>
            {isExpanded ? (
                <LogEntriesList
                    gap="10px"
                    data-automation="google-ads-write-action-log-list"
                >
                    {entries.map((entry) => (
                        <Flex
                            key={entry.id}
                            gap="8px"
                            data-automation={`google-ads-write-action-log-entry-${entry.id}`}
                        >
                            <ActionDot $color={ACTION_TYPE_DOT_COLOR[entry.actionType]} />
                            <Stack gap="2px">
                                <Text
                                    fontVariant="body2"
                                    color={colorsPalettes.carbon[500]}
                                >
                                    {ACTION_TYPE_LABEL[entry.actionType]} at {dayjs(entry.timestamp).format("h:mm A")}
                                </Text>
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon[300]}
                                >
                                    {summarizeOutcome(entry)}
                                </Text>
                            </Stack>
                        </Flex>
                    ))}
                </LogEntriesList>
            ) : null}
        </LogWrapper>
    );
};
